import { useEffect, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import { db } from '../lib/firebase';

/**
 * Subskrypcja kolekcji Firestore w czasie rzeczywistym.
 *
 * Zastępuje sześć niemal identycznych bloków useEffect + onSnapshot + cleanup,
 * które były rozsiane po trzech komponentach.
 *
 * @param {string|null} path - ścieżka kolekcji; `null` wstrzymuje subskrypcję
 *   (np. zanim znane jest userId), co jest ważne, bo collection(db, '') rzuca.
 * @param {object} options
 * @param {Array} options.constraints - ograniczenia zapytania (where/orderBy/limit)
 * @param {string} options.key - identyfikator zestawu ograniczeń. Tablica
 *   `constraints` przy każdym renderze jest nowym obiektem, więc nie nadaje się
 *   na zależność efektu — subskrypcja odpinałaby się i podpinała bez końca.
 *   Efekt reaguje na zmianę tego klucza.
 */
export function useCollection(path, { constraints = [], key = '' } = {}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!path) {
      setData([]);
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    const unsubscribe = onSnapshot(
      query(collection(db, path), ...constraints),
      (snapshot) => {
        setData(snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() })));
        setError(null);
        setLoading(false);
      },
      (snapshotError) => {
        console.error(`Błąd subskrypcji kolekcji "${path}":`, snapshotError);
        setError(snapshotError);
        setLoading(false);
      }
    );

    return unsubscribe;
    // `constraints` celowo poza zależnościami — patrz opis parametru `key`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, key]);

  return { data, loading, error };
}

export default useCollection;
